import { Box, ToggleButton, Tooltip } from '@mui/material';
import React, { useState } from 'react';
import { Tabs, TDeviceDialogType, TDeviceFormValues } from '../types';
import { CustomToggleButtonGroup } from '@/app/components/Styled/StyledToggleButtonGroup';
import TranslationHelper from '@/app/helpers/TranslationHelper';
import { DataTab } from '../DataTab/DataTab';
import { AdvancedTab } from '../AdvancedTab/AdvancedTab';
import { FormikErrors } from 'formik';
import { usePrivileges } from '@/app/state/auth/index.hooks';

interface DeviceDialogProps {
    mode: TDeviceDialogType;
    errors: FormikErrors<TDeviceFormValues>;
    values: TDeviceFormValues;
    setFieldValue: (name: string, value: any) => void;
}

export const DeviceDialogTabs = ({
    mode,
    errors,
    values,
    setFieldValue,
}: DeviceDialogProps) => {
    const [selectedTab, setSelectedTab] = useState<Tabs>('data');
    const privileges = usePrivileges();

    const canEditDevices =
        (mode === 'edit' && privileges.editDevices) ||
        (mode === 'create' && privileges.addDevices);
    const canAccessAdvanced =
        privileges.eToll || privileges.eTollEditRegistrationData;

    const dataTabDisabled = !canEditDevices;
    const advancedTabDisabled = !canAccessAdvanced;

    const handleTabChange = (
        _: React.MouseEvent<HTMLElement>,
        newTab: Tabs | null
    ) => {
        if (newTab !== null) {
            if (newTab === 'data' && dataTabDisabled) return;
            if (newTab === 'advanced' && advancedTabDisabled) return;
            setSelectedTab(newTab);
        }
    };

    React.useEffect(() => {
        if (selectedTab === 'data' && dataTabDisabled) {
            setSelectedTab('advanced');
        } else if (selectedTab === 'advanced' && advancedTabDisabled) {
            setSelectedTab('data');
        }
    }, [dataTabDisabled, advancedTabDisabled, selectedTab]);

    return (
        <Box mt={3}>
            <CustomToggleButtonGroup
                value={selectedTab}
                onChange={handleTabChange}
                fullWidth
                exclusive
            >
                <Tooltip
                    title={
                        dataTabDisabled
                            ? TranslationHelper.translate('Permission required')
                            : ''
                    }
                    disableInteractive
                >
                    <ToggleButton
                        value={'data'}
                        disabled={dataTabDisabled}
                        style={dataTabDisabled ? { pointerEvents: 'auto' } : {}}
                    >
                        {TranslationHelper.translate('Data')}
                    </ToggleButton>
                </Tooltip>
                <Tooltip
                    title={
                        advancedTabDisabled
                            ? TranslationHelper.translate('Permission required')
                            : ''
                    }
                    disableInteractive
                >
                    <ToggleButton
                        value={'advanced'}
                        disabled={advancedTabDisabled}
                        style={
                            advancedTabDisabled ? { pointerEvents: 'auto' } : {}
                        }
                    >
                        {TranslationHelper.translate('Advanced')}
                    </ToggleButton>
                </Tooltip>
            </CustomToggleButtonGroup>

            <Box mt={3}>
                {selectedTab === 'data' && !dataTabDisabled && (
                    <DataTab
                        mode={mode}
                        errors={errors.data}
                        values={values}
                        setFieldValue={setFieldValue}
                    />
                )}
                {selectedTab === 'advanced' && !advancedTabDisabled && (
                    <AdvancedTab values={values} errors={errors.advanced} />
                )}
            </Box>
        </Box>
    );
};
